/**
 * Built-in starting points for a new poll (#76).
 *
 * Story #48 already shipped the user-authored half of "templates": duplicating
 * a poll deep-copies its structure under fresh ids, which is what "save this as
 * a template" means in practice. What is left is somewhere to start for a
 * workspace with nothing to copy yet.
 *
 * The catalogue is text only — no ids. Ids are generated server-side when a
 * poll is seeded (see `screensForStarter` in packages/api/src/lib/starters.ts),
 * so creating the same starter twice shares no id, which is the rule
 * `copyScreens` already follows for a duplicate.
 *
 * It lives in shared rather than behind an endpoint so the console can render
 * the picker from the same definitions the API seeds from. A second source for
 * the labels would be free to disagree with the questions someone actually gets.
 *
 * Every starter holds multiple-choice questions only. That is the one kind the
 * authoring model expresses without a `type` discriminator on the question, and
 * it keeps a seeded poll identical in shape to every poll authored before
 * question types existed. It is a limit on what a starter may contain, not a
 * dependency on anything unbuilt.
 */

export interface StarterQuestion {
  text: string;
  options: string[];
}

export interface StarterScreen {
  questions: StarterQuestion[];
}

export interface Starter {
  /** Shown in the picker. */
  label: string;
  /** One line under the label, saying what the room is being asked. */
  description: string;
  screens: StarterScreen[];
}

export const STARTERS = {
  icebreaker: {
    label: 'Icebreaker',
    description: 'Three quick questions to warm a room up before the real agenda.',
    screens: [
      {
        questions: [
          {
            text: "How's your energy right now?",
            options: ['Wired', 'Good', 'Fading', 'Send coffee'],
          },
        ],
      },
      {
        questions: [
          {
            text: 'Where are you joining from?',
            options: ['At my desk', 'At home', 'In the room', 'Somewhere in transit'],
          },
        ],
      },
      {
        questions: [
          {
            text: 'Pick the snack this team runs on',
            options: ['Crisps', 'Fruit', 'Chocolate', 'Something hot'],
          },
        ],
      },
    ],
  },
  retro: {
    label: 'Team retro',
    description: 'Take the temperature of the last sprint before anyone talks about it.',
    screens: [
      {
        questions: [
          {
            text: 'How did this sprint feel?',
            options: ['Our best yet', 'Solid', 'Bumpy', 'Rough'],
          },
        ],
      },
      {
        questions: [
          {
            text: 'What slowed us down most?',
            options: [
              'Unclear scope',
              'Waiting on reviews',
              'Too many meetings',
              'Flaky tooling',
              'Nothing much',
            ],
          },
        ],
      },
      {
        questions: [
          {
            text: 'What should we change first?',
            options: ['Plan smaller', 'Review faster', 'Meet less', 'Fix the tooling'],
          },
        ],
      },
    ],
  },
  feedback: {
    label: 'Event feedback',
    description:
      'Close a session with the three questions worth asking while the room is still in it.',
    screens: [
      {
        questions: [
          {
            text: 'How was the session overall?',
            options: ['Excellent', 'Good', 'OK', 'Poor'],
          },
        ],
      },
      {
        questions: [
          {
            text: 'Was the pace right?',
            options: ['Too slow', 'About right', 'Too fast'],
          },
        ],
      },
      {
        questions: [
          {
            text: 'Would you recommend this to a colleague?',
            options: ['Definitely', 'Probably', 'Probably not', 'Definitely not'],
          },
        ],
      },
    ],
  },
} satisfies Record<string, Starter>;

export type StarterName = keyof typeof STARTERS;

export const STARTER_NAMES = Object.keys(STARTERS) as StarterName[];

/**
 * Whether a client-supplied value names a starter we ship.
 *
 * An unrecognised name is not an error anywhere: the create route falls back to
 * today's blank poll, so a console built against a newer catalogue degrades to
 * the behaviour it had before starters existed rather than failing the create.
 */
export function isStarterName(value: unknown): value is StarterName {
  return typeof value === 'string' && Object.hasOwn(STARTERS, value);
}
